import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, COLOR_THEMES } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { 
  LayoutDashboard, Users, Printer, FileText, Package, Wrench, 
  History, AlertCircle, BookOpen, Settings, LogOut, Menu, X,
  Calendar, Database, MoreHorizontal, ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/customers', label: 'ΠΕΛΑΤΕΣ', icon: Users },
  { path: '/machines', label: 'ΜΗΧΑΝΗΜΑΤΑ', icon: Printer },
  { path: '/readings', label: 'ΜΕΤΡΗΣΕΙΣ', icon: FileText },
  { path: '/dispatches', label: 'ΑΠΟΣΤΟΛΕΣ', icon: Package },
  { path: '/service', label: 'SERVICE', icon: Wrench },
  { path: '/history', label: 'ΙΣΤΟΡΙΚΟ', icon: History },
  { path: '/pending', label: 'ΕΚΚΡΕΜΟΤΗΤΕΣ', icon: AlertCircle },
  { path: '/catalog', label: 'ΚΑΤΑΛΟΓΟΣ ΜΗΧ.', icon: BookOpen },
  { path: '/consumables', label: 'ΑΝΑΛΩΣΙΜΑ', icon: Settings },
  { path: '/reports', label: 'REPORTS', icon: Calendar },
  { path: '/backup', label: 'BACKUP', icon: Database },
];

// Primary tabs for bottom nav (most used)
const bottomNavItems = [
  { path: '/dashboard', label: 'Αρχική', icon: LayoutDashboard },
  { path: '/customers', label: 'Πελάτες', icon: Users },
  { path: '/machines', label: 'Μηχαν.', icon: Printer },
  { path: '/dispatches', label: 'Αποστολ.', icon: Package },
];

// Secondary items (shown in "More" panel)
const moreNavItems = [
  { path: '/readings', label: 'ΜΕΤΡΗΣΕΙΣ', icon: FileText },
  { path: '/bulk-readings', label: 'ΜΑΖΙΚΗ ΕΙΣΑΓΩΓΗ', icon: ClipboardList, adminOnly: true },
  { path: '/service', label: 'SERVICE', icon: Wrench },
  { path: '/history', label: 'ΙΣΤΟΡΙΚΟ', icon: History },
  { path: '/pending', label: 'ΕΚΚΡΕΜΟΤΗΤΕΣ', icon: AlertCircle },
  { path: '/catalog', label: 'ΚΑΤΑΛΟΓΟΣ ΜΗΧ.', icon: BookOpen },
  { path: '/consumables', label: 'ΑΝΑΛΩΣΙΜΑ', icon: Settings },
  { path: '/reports', label: 'REPORTS', icon: Calendar },
  { path: '/backup', label: 'BACKUP', icon: Database },
];

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const colorPickerRef = useRef(null);
  const moreMenuRef = useRef(null);
  const { user, logout, logoutOperator, companyName, role, operatorName, isAdmin } = useAuth();
  const { colorTheme, changeColorTheme, currentColors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect mobile
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setShowMoreMenu(false);
    }
  }, [location.pathname, isMobile]);

  // Close color picker / more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isInMoreMenu = moreNavItems.some(item => item.path === location.pathname);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar (hidden on mobile) */}
      {!isMobile && (
        <aside
          className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 overflow-hidden ${sidebarOpen ? 'w-64' : 'w-20'} text-white shadow-2xl`}
          style={{ background: `linear-gradient(to bottom, ${currentColors.primary}, ${currentColors.primaryDark})` }}
        >
          <div className="h-16 flex items-center justify-between px-4 border-b border-white/10 min-w-[256px]">
            <h1 className="text-xl font-bold truncate">{companyName || 'MyPrinterManager'}</h1>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-white/10" data-testid="sidebar-toggle">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1 min-w-[256px]">
            {menuItems.filter(item => !item.adminOnly || isAdmin).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button key={item.path} onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    isActive ? 'bg-white shadow-lg scale-105' : 'text-white hover:bg-white/10 hover:scale-105'
                  }`}
                  style={isActive ? { color: currentColors.primary } : {}}
                  data-testid={`nav-${item.path.slice(1)}`}>
                  <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-medium truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/10 min-w-[256px]">
            <div className="mb-2">
              <p className="text-xs text-white/60 mb-1">Χειριστής</p>
              <p className="text-sm font-medium truncate">{operatorName || user?.username}{isAdmin && ' (Admin)'}</p>
            </div>
            {role === 'super_admin' && (
              <Button variant="ghost" onClick={() => { logout(); navigate('/login'); }}
                className="w-full text-white hover:bg-blue-500/20 hover:text-blue-200 justify-start mb-1"
                data-testid="back-to-companies-btn">
                <Users className="h-5 w-5" /><span className="ml-2">Αλλαγή Εταιρίας</span>
              </Button>
            )}
            <Button variant="ghost" onClick={() => { logoutOperator(); navigate('/verify'); }}
              className="w-full text-white hover:bg-yellow-500/20 hover:text-yellow-200 justify-start mb-1"
              data-testid="change-operator-btn">
              <Users className="h-5 w-5" /><span className="ml-2">Αλλαγή Χειριστή</span>
            </Button>
            <Button variant="ghost" onClick={handleLogout}
              className="w-full text-white hover:bg-red-500/20 hover:text-red-200 justify-start"
              data-testid="logout-btn">
              <LogOut className="h-5 w-5" /><span className="ml-2">Αποσύνδεση</span>
            </Button>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${!isMobile && sidebarOpen ? 'md:ml-64' : !isMobile ? 'md:ml-20' : 'ml-0'}`}>
        {/* Top Bar */}
        <header className="h-14 md:h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-3 md:px-6 sticky top-0 z-20 shadow-sm transition-colors">
          <div className="flex items-center gap-3">
            {!isMobile && !sidebarOpen && (
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}
                className="text-gray-600 hover:bg-gray-100" data-testid="hamburger-btn">
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <div className="min-w-0">
              <h2 className="text-base md:text-xl font-semibold text-gray-800 dark:text-white truncate">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                {new Date().toLocaleDateString('el-GR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <div className="relative" ref={colorPickerRef}>
              <button data-testid="color-theme-toggle" onClick={() => setShowColorPicker(!showColorPicker)}
                className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 hover:scale-110 shadow-sm"
                style={{ backgroundColor: currentColors.primary }} title="Αλλαγή χρώματος" />
              {showColorPicker && (
                <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 z-50 animate-in fade-in-0 slide-in-from-top-2 duration-200">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Χρώμα θέματος</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(COLOR_THEMES).map(([key, theme]) => (
                      <button key={key} data-testid={`color-theme-${key}`}
                        onClick={() => { changeColorTheme(key); setShowColorPicker(false); }}
                        className={`w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110 ${
                          colorTheme === key ? 'ring-2 ring-offset-2 scale-110' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: theme.primary, borderColor: colorTheme === key ? theme.primary : undefined, '--tw-ring-color': theme.primary }}
                        title={theme.name} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ThemeToggle />
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              {new Date().toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {/* Mobile: operator info */}
            {isMobile && operatorName && (
              <span className="text-[10px] text-gray-500 truncate max-w-[80px]">{operatorName}</span>
            )}
          </div>
        </header>

        {/* Page Content - add bottom padding on mobile for bottom nav */}
        <main className={`p-3 md:p-6 overflow-x-hidden ${isMobile ? 'pb-20' : ''}`}>
          {children}
        </main>
      </div>

      {/* ============ MOBILE BOTTOM NAV BAR ============ */}
      {isMobile && (
        <>
          {/* More menu panel */}
          {showMoreMenu && (
            <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)}>
              <div className="absolute bottom-16 left-0 right-0 bg-black/40" style={{ top: 0 }} />
              <div ref={moreMenuRef}
                className="absolute bottom-16 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl border-t border-gray-200 animate-in slide-in-from-bottom-4 duration-200 max-h-[70vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="p-2 border-b border-gray-100">
                  <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-2" />
                  <p className="text-xs text-center text-gray-500 font-medium">
                    {operatorName || user?.username}{isAdmin && ' (Admin)'} &bull; {companyName}
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-1 p-3">
                  {moreNavItems.filter(item => !item.adminOnly || isAdmin).map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <button key={item.path}
                        onClick={() => { navigate(item.path); setShowMoreMenu(false); }}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
                          isActive ? 'bg-blue-50' : 'hover:bg-gray-50 active:bg-gray-100'
                        }`}
                        style={isActive ? { color: currentColors.primary } : {}}
                        data-testid={`more-nav-${item.path.slice(1)}`}>
                        <Icon className="h-5 w-5" style={isActive ? { color: currentColors.primary } : { color: '#6b7280' }} />
                        <span className={`text-[10px] leading-tight text-center font-medium ${isActive ? '' : 'text-gray-600'}`}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Bottom Nav Bar */}
          <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
            data-testid="bottom-nav">
            <div className="flex items-center justify-around h-16 px-1">
              {/* Logout/Exit button */}
              <button
                onClick={() => { logoutOperator(); navigate('/verify'); }}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative transition-all active:scale-95"
                data-testid="bottom-nav-exit">
                <LogOut className="h-5 w-5 text-red-400" />
                <span className="text-[10px] font-medium text-red-400">Έξοδος</span>
              </button>
              {bottomNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button key={item.path}
                    onClick={() => navigate(item.path)}
                    className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative transition-all active:scale-95"
                    data-testid={`bottom-nav-${item.path.slice(1)}`}>
                    {isActive && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-b-full"
                        style={{ backgroundColor: currentColors.primary }} />
                    )}
                    <Icon className="h-5 w-5 transition-colors"
                      style={{ color: isActive ? currentColors.primary : '#9ca3af' }} />
                    <span className={`text-[10px] font-medium transition-colors ${
                      isActive ? '' : 'text-gray-400'
                    }`} style={isActive ? { color: currentColors.primary } : {}}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
              {/* More button */}
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative transition-all active:scale-95"
                data-testid="bottom-nav-more">
                {isInMoreMenu && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-b-full"
                    style={{ backgroundColor: currentColors.primary }} />
                )}
                <MoreHorizontal className="h-5 w-5 transition-colors"
                  style={{ color: (showMoreMenu || isInMoreMenu) ? currentColors.primary : '#9ca3af' }} />
                <span className={`text-[10px] font-medium transition-colors ${
                  (showMoreMenu || isInMoreMenu) ? '' : 'text-gray-400'
                }`} style={(showMoreMenu || isInMoreMenu) ? { color: currentColors.primary } : {}}>
                  Άλλα
                </span>
              </button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
};

export default MainLayout;
